<template>
    <vvo v-slot="v" :key="newChannel.channelIdx" :class="{ 'channel-popup-panel-mode': mode === 'panel' }">
        <!-- 헤더 -->
        <div class="row form-group">
            <div class="col detail-title">
                <span class="title">{{ newChannel.channelName || "통신채널" }}</span>
                <span class="badge title"> {{ newChannel.channelType }} </span>
                <span class="badge id"> {{ newChannel.channelIdx || "신규" }} </span>
            </div>
        </div>

        <!-- 탭 컨테이너 -->
        <div v-if="!isEmpty(channelIdx)" class="tab-container overflowStyle">
            <!-- 탭 버튼 -->
            <ul class="custom-tabs">
                <li v-for="(tab, index) in tabs" :key="index" class="custom-tab"
                    :class="{ active: activeTab === index }" @click="handleTabClick(index)">
                    <span class="mr-1"><app-icon :icon="tab.icon" /></span>
                    <span>{{ tab.label }}</span>
                </li>
            </ul>

            <!-- 탭 콘텐츠 -->
            <div class="tab-content-container p-3 mb-0 overflowStyle">
                <!-- 하위 계측기 목록 탭 -->
                <div v-if="activeTab === 0" :key="'tab-0'" class="equipment-list-content">
                    <div v-if="childData && childData.children && childData.children.length > 0"
                        class="equipment-container">
                        <!-- 서브 탭 버튼 및 엑셀 다운로드 버튼 -->
                        <div class="controls-wrapper">
                            <!-- 계측기 정보 탭 -->
                            <div class="facility-mode-controls">
                                <label class="tab-label">표시 방법 :</label>
                                <select v-model="activeSubTab" class="tab-select" @change="handleSubTabChange">
                                    <option v-for="(subTab, index) in subTabs" :key="index" :value="index">
                                        {{ subTab.label }}
                                    </option>
                                </select>
                            </div>
                            <!-- 엑셀 작업 -->
                            <div v-if="activeSubTab === 1" class="excel-section">
                                <button class="excel-btn upload" @click="triggerFileUpload">
                                    <i class="fa fa-upload"></i> 관리정보 업로드
                                </button>
                                <button class="excel-btn download" @click="downloadFacilityOpInfo">
                                    <i class="fa fa-download"></i> 관리정보 다운로드
                                </button>
                            </div>
                        </div>
                        <input ref="fileInput" type="file" @change="handleFileChange" accept=".xlsx,.xls"
                            style="display: none" />
                        <div v-if="activeSubTab === 0">
                            <div class="table-container">
                                <TableList :columns="equipmentColumns" :rows="equipmentRows"
                                    :trans-codes="channelTransCodes" :key-field="'facilityIdx'" :mode="'view'"
                                    :useFilter="true" :isSort="true" :fixedHeader="true" :customMaxHeight="'400px'"
                                    :useMaxHeight="true" :borderRadius="'0px 0px 6px 6px'"
                                    @row-click="handleRowClick" />
                            </div>
                        </div>

                        <div v-if="activeSubTab === 1">
                            <div class="table-container">
                                <TableList :key="`management-table-${selectedGroupField || 'no-group'}`"
                                    :columns="facilityManagementColumns" :rows="filteredFacilityManagementRows"
                                    :key-field="'facilityIdx'" :mode="'view'" :use-filter="true" :is-sort="true"
                                    :fixed-header="true" :custom-max-height="'480px'"
                                    :trans-codes="facilityManagementTransCodes" :borderRadius="'0px 0px 6px 6px'"
                                    @row-click="handleRowClick" />
                            </div>
                        </div>
                    </div>

                    <div v-else class="no-equipment">
                        <div class="no-data-message">
                            <i class="fa fa-info-circle"></i>
                            <span>등록된 장비가 없습니다.</span>
                        </div>
                    </div>
                </div>

                <!-- 통신채널 정보 탭 -->
                <div v-else-if="activeTab === 1" :key="'tab-1'" class="mt-2">
                    <!-- Common 1st Line -->
                    <div class="row form-group">
                        <div class="col-6">
                            <label>{{ $t("통신채널 명칭") }}</label>
                            <valid-input :vid="'통신채널 명칭'" :inputType="'text'" :inputValue.sync="newChannel.channelName"
                                :rules="rules.COLLECT_DATA_CHANNEL_NAME_RULE"
                                :errorMessage="validMessage.COMMON_BOTH_LENGTH_VALID_MESSAGE(1, 20)"
                                :placeholder="$t('통신채널 명칭을 입력하세요.')" :isMessage="true">
                            </valid-input>
                        </div>
                        <div class="col-4">
                            <label>{{ $t("통신 방식") }}</label>
                            <select class="form-control" v-model="newChannel.channelType" @change="selectChannelType()"
                                :disabled="isUpdate">
                                <option value="ModbusRtu">ModbusRtu</option>
                                <option value="ModbusTcp">ModbusTcp</option>
                                <option value="RS485">RS485</option>
                                <option value="KieMecs">TheKIE OpenAPI Center (KIEMecs)</option>
                                <option value="IBeems">IBeems {{ $t("API") }}</option>
                                <option value="KevinLab">KevinLab {{ $t("클라우드") }}</option>
                                <option value="Aquaman">Aquaman {{ $t("데이터 서버") }}</option>
                                <option value="SocketTcp">SocketTcp</option>
                                <option value="BacnetTcp">BacnetTcp</option>
                                <option value="Mqtt">Mqtt</option>
                                <option value="Virtual">Virtual</option>
                            </select>
                        </div>
                        <div class="col-lg-2">
                            <label>{{ $t("채널 사용") }}</label>
                            <div class="checkbox checkbox-css">
                                <input type="checkbox" :id="'newGroupUse_' + newChannel.enable"
                                    v-model="newChannel.enable" />
                                <label :for="'newGroupUse_' + newChannel.enable">{{ $t("사용") }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- Common 2nd Line -->
                    <div class="row form-group">
                        <div class="col-3">
                            <label>{{ $t("읽기 주기 (초)") }}</label>
                            <select class="form-control" v-model="newChannel.channelOption.scan">
                                <option value="10000">10 sec</option>
                                <option value="30000">30 sec</option>
                                <option value="60000">1 min</option>
                                <option value="120000">2 min</option>
                                <option value="180000">3 min</option>
                                <option value="240000">4 min</option>
                                <option value="300000">5 min</option>
                                <option value="1500000">15 min</option>
                                <option value="3600000">1 hour</option>
                                <option value="7200000">2 hour</option>
                                <option value="10800000">3 hour</option>
                            </select>
                        </div>
                        <div class="col-3">
                            <label>{{ $t("타임아웃") }} (ms)</label>
                            <select class="form-control" v-model="newChannel.channelOption.timeout">
                                <option value="300">300 ms</option>
                                <option value="500">500 ms</option>
                                <option value="1000">1 sec</option>
                                <option value="3000">3 sec</option>
                                <option value="5000">5 sec</option>
                                <option value="10000">10 sec</option>
                                <option value="60000">1 min</option>
                                <option value="180000">3 min</option>
                            </select>
                        </div>
                        <div class="col-4">
                            <label>{{ $t("보고 방식") }}</label>
                            <select class="form-control" v-model="newChannel.channelOption.reporting">
                                <option value="WebAPI">xEMS WebAPI</option>
                                <option value="MQTT">xEMS MQTT</option>
                            </select>
                        </div>
                        <div class="col-2">
                            <label>{{ $t("가상 모드") }}</label>
                            <div class="checkbox checkbox-css">
                                <input type="checkbox" :id="'isVirtualMode_' + newChannel.channelIdx"
                                    v-model="newChannel.channelOption.isVirtualMode" />
                                <label :for="'isVirtualMode_' + newChannel.channelIdx">{{ $t("사용") }}</label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 부하 목록 관리 탭 -->
                <div v-else-if="activeTab === 2" :key="'tab-2'" class="load-management-content">
                    <!-- 파일 업로드 섹션 -->
                    <div class="upload-section">
                        <div class="upload-header">
                            <h6 class="section-title">
                                <i class="fa fa-upload"></i>
                                엑셀 파일 업로드
                            </h6>
                        </div>
                        <div class="upload-area">
                            <div class="file-input-group">
                                <div class="file-display">
                                    <i class="fa fas fa-file-excel file-icon"></i>
                                    <span class="file-name">{{ fileName || "파일을 선택해주세요" }}</span>
                                </div>
                                <div class="button-group">
                                    <button class="btn btn-outline-primary btn-sm" @click="selectFile">
                                        <i class="fa fa-folder-open"></i> 파일 선택
                                    </button>
                                    <button class="btn btn-primary btn-sm" @click="uploadFile" :disabled="!fileName">
                                        <i class="fa fa-upload"></i> 업로드 및 헤더 매핑
                                    </button>
                                    <button class="btn btn-outline-info btn-sm" @click="downloadLoadList">
                                        <i class="fa fa-download"></i> 다운로드
                                    </button>
                                </div>
                                <input type="file" ref="fileInput" class="d-none" @change="handleFileUpload" />
                            </div>
                        </div>
                    </div>

                    <!-- 헤더 매핑 섹션 -->
                    <div v-if="headerMappings.length > 0 && isEmpty(excelUploadErrorList)" class="mapping-section">
                        <div class="mapping-header">
                            <h6 class="section-title">
                                <i class="fa fa-link"></i>
                                헤더 매핑 설정
                            </h6>
                            <!-- <button class="btn btn-success btn-sm" @click="saveMappingInfo">
                                <i class="fa fa-save"></i> 저장
                            </button> -->
                        </div>
                        <div class="mapping-list">
                            <div class="mapping-grid">
                                <div v-for="(mapping, index) in headerMappings" :key="index" class="mapping-item"
                                    :class="{
                                        filled: mapping.convertHeader && mapping.convertHeader !== null,
                                    }">
                                    <div class="mapping-source">
                                        <div class="mapping-value">{{ mapping.excelHeader }}</div>
                                        <div class="mapping-dropdown-container"
                                            v-if="showSubDesc(mapping.convertHeader)">
                                            <i class="fa fa-ellipsis-h mapping-ellipsis"
                                                @mouseenter="showDropdown(index)" @mouseleave="hideDropdown()" />
                                            <div v-show="activeDropdownIndex === index" class="mapping-dropdown"
                                                @mouseenter="showDropdown(index)" @mouseleave="hideDropdown()">
                                                <div v-for="(item, itemIndex) in getSubDescListItems(
                                                    mapping.convertHeader
                                                )" :key="itemIndex" class="dropdown-item">
                                                    {{ item.text }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mapping-target">
                                        <b-form-select v-model="mapping.convertHeader" :options="convertHeaderOptions"
                                            class="mapping-select" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 오류 목록 섹션 -->
                    <div v-if="!isEmpty(excelUploadErrorList)" class="error-section">
                        <div class="error-header">
                            <h6 class="section-title text-danger">
                                <i class="fa fa-exclamation-triangle"></i>
                                업로드 오류
                            </h6>
                        </div>
                        <div class="error-list">
                            <div v-for="(error, index) in excelUploadErrorList" :key="index" class="error-item">
                                <div class="error-row">
                                    <span class="error-badge">행 {{ error.row }}</span>
                                </div>
                                <div class="error-message">
                                    {{ error.error }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- 기존 통신채널 정보 (신규 등록시) -->
        <template v-else>
            <!-- Common 1st Line -->
            <div class="row form-group">
                <div class="col-6">
                    <label>{{ $t("통신채널 명칭") }}</label>
                    <valid-input :vid="'통신채널 명칭'" :inputType="'text'" :inputValue.sync="newChannel.channelName"
                        :rules="rules.COLLECT_DATA_CHANNEL_NAME_RULE"
                        :errorMessage="validMessage.COMMON_BOTH_LENGTH_VALID_MESSAGE(1, 20)"
                        :placeholder="$t('통신채널 명칭을 입력하세요.')" :isMessage="true">
                    </valid-input>
                </div>
                <div class="col-4">
                    <!-- 채널타입은 변경할 수 없다. 장비의 드라이버가 잘못된다. -->
                    <!-- ADD_DRIVER -->
                    <label>{{ $t("통신 방식") }}</label>
                    <select class="form-control" v-model="newChannel.channelType" @change="selectChannelType()"
                        :disabled="isUpdate">
                        <option value="ModbusRtu">ModbusRtu</option>
                        <option value="ModbusTcp">ModbusTcp</option>
                        <option value="RS485">RS485</option>
                        <option value="KieMecs">TheKIE OpenAPI Center (KIEMecs)</option>
                        <option value="IBeems">IBeems {{ $t("API") }}</option>
                        <option value="KevinLab">KevinLab {{ $t("클라우드") }}</option>
                        <option value="Aquaman">Aquaman {{ $t("데이터 서버") }}</option>

                        <option value="SocketTcp">SocketTcp</option>
                        <option value="SocketUdp">SocketUdp</option>
                        <option value="BacnetTcp">BacnetTcp</option>
                        <option value="Mqtt">Mqtt</option>

                        <option value="Virtual">Virtual</option>
                    </select>
                </div>
                <div class="col-lg-2">
                    <label>{{ $t("채널 사용") }}</label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'newGroupUse_' + newChannel.enable" v-model="newChannel.enable" />
                        <label :for="'newGroupUse_' + newChannel.enable">{{ $t("사용") }}</label>
                    </div>
                </div>
            </div>

            <!-- Common 2nd Line -->
            <div class="row form-group">
                <div class="col-3">
                    <label>{{ $t("읽기 주기 (초)") }}</label>
                    <select class="form-control" v-model="newChannel.channelOption.scan">
                        <option value="10000">10 sec</option>
                        <option value="30000">30 sec</option>
                        <option value="60000">1 min</option>
                        <option value="120000">2 min</option>
                        <option value="180000">3 min</option>
                        <option value="240000">4 min</option>
                        <option value="300000">5 min</option>
                        <option value="1500000">15 min</option>
                        <option value="3600000">1 hour</option>
                        <option value="7200000">2 hour</option>
                        <option value="10800000">3 hour</option>
                    </select>
                </div>
                <div class="col-3">
                    <label>{{ $t("타임아웃") }} (ms)</label>
                    <select class="form-control" v-model="newChannel.channelOption.timeout">
                        <option value="300">300 ms</option>
                        <option value="500">500 ms</option>
                        <option value="1000">1 sec</option>
                        <option value="3000">3 sec</option>
                        <option value="5000">5 sec</option>
                        <option value="10000">10 sec</option>
                        <option value="60000">1 min</option>
                        <option value="180000">3 min</option>
                    </select>
                </div>
                <div class="col-4">
                    <label>{{ $t("보고 방식") }}</label>
                    <select class="form-control" v-model="newChannel.channelOption.reporting">
                        <option value="WebAPI">xEMS WebAPI</option>
                        <option value="MQTT">xEMS MQTT</option>
                    </select>
                </div>
                <div class="col-2">
                    <label>{{ $t("가상 모드") }}</label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'isVirtualMode_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.isVirtualMode" />
                        <label :for="'isVirtualMode_' + newChannel.channelIdx">{{ $t("사용") }}</label>
                    </div>
                </div>
            </div>
        </template>

        <!-- ModbusRTU || RS485 Case (1st Line)-->
        <!-- ADD_DRIVER -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && (newChannel.channelType === 'ModbusRtu' || newChannel.channelType === 'RS485')
                : newChannel.channelType === 'ModbusRtu' || newChannel.channelType === 'RS485'
        ">
            <!-- ModbusRTU || RS485 Case (1st Line)-->
            <div class="row form-group">
                <div class="col-6">
                    <label> {{ $t("통신 포트(COM)") }} </label>
                    <valid-input :vid="'통신 포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.comPort.dev"
                        :rules="rules.COLLECT_DATA_MODBUS_RS_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(1, 255)"
                        :placeholder="$t('통신 포트(COM)를 입력하세요.')" :isMessage="true">
                    </valid-input>
                </div>
                <div class="col-4">
                    <label> {{ $t("비트/초 (bps)") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.comPort.baudRate">
                        <option value="4800">4800</option>
                        <option value="9600">9600</option>
                        <option value="19200">19200</option>
                        <option value="38400">38400</option>
                        <option value="115200">115200</option>
                    </select>
                </div>
            </div>
            <!-- ModbusRTU || RS485 Case (2nd Line)-->
            <div class="row form-group"
                v-if="newChannel.channelType === 'ModbusRtu' || newChannel.channelType === 'RS485'">
                <div class="col-3">
                    <label> {{ $t("데이터 비트") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.comPort.dataBits">
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                        <option value="9">9</option>
                    </select>
                </div>
                <div class="col-3">
                    <label> {{ $t("패리티") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.comPort.parity">
                        <option value="none">none</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                    </select>
                </div>
                <div class="col-3">
                    <label> {{ $t("정지 비트") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.comPort.stopBits">
                        <option value="1">1</option>
                        <option value="2">2</option>
                    </select>
                </div>
                <div class="col-3">
                    <label> {{ $t("자동연결해제") }} </label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'autoClose_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.setting.comPort.autoClose" />
                        <label :for="'autoClose_' + newChannel.channelIdx">
                            {{ $t("사용") }}
                        </label>
                    </div>
                </div>
            </div>
        </template>

        <!-- ModbusTCP Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'ModbusTcp'
                : newChannel.channelType === 'ModbusTcp'
        ">
            <div class="row form-group">
                <div class="col-5">
                    <label> {{ $t("호스트명") }}</label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.host"
                        :rules="rules.COLLECT_DATA_MODBUS_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Host">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.host" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-4">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_MODBUS_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="number" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("자동연결해제") }} </label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'autoClose_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.setting.server.autoClose" />
                        <label :for="'autoClose_' + newChannel.channelIdx">
                            {{ $t("사용") }}
                        </label>
                    </div>
                </div>
            </div>
        </template>

        <!-- BacnetTcp Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'BacnetTcp'
                : newChannel.channelType === 'BacnetTcp'
        ">
            <div class="row form-group">
                <div class="col-5">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.host"
                        :rules="rules.COLLECT_DATA_MODBUS_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Host">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.host" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-4">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_MODBUS_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="number" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("자동연결해제") }} </label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'autoClose_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.setting.server.autoClose" />
                        <label :for="'autoClose_' + newChannel.channelIdx">
                            {{ $t("사용") }}
                        </label>
                    </div>
                </div>
            </div>
        </template>

        <!-- Socket TCP Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'SocketTcp'
                : newChannel.channelType === 'SocketTcp'
        ">
            <div class="row form-group">
                <div class="col-5">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.host"
                        :rules="rules.COLLECT_DATA_MODBUS_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Host">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.host" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-4">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_MODBUS_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="number" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("자동연결해제") }} </label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'autoClose_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.setting.server.autoClose" />
                        <label :for="'autoClose_' + newChannel.channelIdx">
                            {{ $t("사용") }}
                        </label>
                    </div>
                </div>
            </div>
            <div class="row form-group">
                <div class="col-5">
                    <label> {{ $t("시리얼 번호") }} </label>
                    <valid-input :vid="'시리얼 번호'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.serial"
                        :rules="'required|min:1|min_value:0|max:10|max_value:9999999999'"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 9999999999)"
                        :placeholder="$t('시리얼 번호를 입력하세요.')" :isMessage="true">
                    </valid-input>
                </div>
            </div>
        </template>

        <!-- Socket UDP Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'SocketUdp'
                : newChannel.channelType === 'SocketUdp'
        ">
            <div class="row form-group">
                <div class="col-5">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.host"
                        :rules="rules.COLLECT_DATA_MODBUS_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Host">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.host" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-4">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_MODBUS_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="number" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("자동연결해제") }} </label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'autoClose_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.setting.server.autoClose" />
                        <label :for="'autoClose_' + newChannel.channelIdx">
                            {{ $t("사용") }}
                        </label>
                    </div>
                </div>
            </div>
        </template>
        <!-- IBeems Case -->
        <template v-if="newChannel.channelType === 'IBeems' && activeTab === 1">
            <div class="row form-group">
                <div class="col-3">
                    <label> {{ $t("프로토콜") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.server.protocol">
                        <option value="http">http</option>
                        <option value="https">https</option>
                    </select>
                </div>
                <div class="col-6">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.hostName"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="HostName">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.hostName" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- 
          <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
            </div>

            <div class="row form-group">
                <div class="col-6">
                    <label> {{ $t("서비스 토큰") }} </label>
                    <valid-input :vid="'서비스 토큰'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.serviceToken"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_ADDR_RULE" :placeholder="$t('서비스 토큰을 입력하세요.')"
                        :NonStatus="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="인증 URL">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('인증을 위한 경로를 입력하세요.')" v-model="newChannel.channelOption.setting.server.authUrl" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-4">
                    <label>{{ $t("데이터 유효기간") }}</label>
                    <select class="form-control" v-model="newChannel.channelOption.setting.server.validDataTime">
                        <option value="9999999">None</option>
                        <option value="60">1 min</option>
                        <option value="180">3 min</option>
                        <option value="300">5 min</option>
                        <option value="600">10 min</option>
                        <option value="1800">30 min</option>
                        <option value="3600">1 hour</option>
                        <option value="7200">2 hour</option>
                        <option value="10800">3 hour</option>
                    </select>
                </div>
            </div>
        </template>

        <!-- KevinLab Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'KevinLab'
                : newChannel.channelType === 'KevinLab'
        ">
            <div class="row form-group">
                <div class="col-3">
                    <label> {{ $t("프로토콜") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.server.protocol">
                        <option value="http">http</option>
                        <option value="https">https</option>
                    </select>
                </div>
                <div class="col-6">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.hostName"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="HostName">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.hostName" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- 
          <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
            </div>

            <div class="row form-group">
                <div class="col-6">
                    <label> {{ $t("인증 경로") }} </label>
                    <valid-input :vid="'인증 경로'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authUrl"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_ADDR_RULE" :placeholder="$t('인증을 위한 경로를 입력하세요.')"
                        :NonStatus="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="인증 URL">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('인증을 위한 경로를 입력하세요.')" v-model="newChannel.channelOption.setting.server.authUrl" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("암호화 방식") }} </label>
                    <select name="" class="form-control"
                        v-model="newChannel.channelOption.setting.server.encodingMethod">
                        <option value="base64">base64</option>
                        <option value="hex">hex</option>
                    </select>
                </div>
                <div class="col-3">
                    <label> {{ $t("인증 타입") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.server.authType">
                        <option value="Cookie">Cookie</option>
                        <option value="JWT">JWT</option>
                    </select>
                </div>
            </div>

            <div class="row form-group">
                <div class="col-4">
                    <label> {{ $t("암호화 방식") }} </label>
                    <select name="" class="form-control"
                        v-model="newChannel.channelOption.setting.server.encryptMethod">
                        <option value="None">{{ $t("미사용") }}</option>
                        <option value="aes-256-cbc">aes-256-cbc</option>
                    </select>
                </div>
                <div class="col-8">
                    <label> {{ $t("암호화 키") }} </label>
                    <valid-input :vid="'암호화 키'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.encryptKey"
                        :rules="rules.COLLECT_DATA_KL_PASSKEY_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(1, 50)" :placeholder="$t('암호화 키 입력')"
                        :NonStatus="true">
                    </valid-input>

                    <!-- <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('암호화 키 입력')" v-model="newChannel.channelOption.setting.server.encryptKey" /> -->
                </div>
            </div>

            <div class="row form-group">
                <div class="col-6">
                    <label> {{ $t("인증 아이디") }} </label>
                    <valid-input :vid="'인증 아이디'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authId"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_ID_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(1, 50)"
                        :placeholder="$t('인증 아이디 입력')" :NonStatus="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="authId">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('인증 아이디 입력')" v-model="newChannel.channelOption.setting.server.authId" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-6">
                    <label> {{ $t("인증 비밀번호") }} </label>
                    <valid-input :vid="'인증 비밀번호'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authSecret"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_PASSWORD_RULE" :placeholder="$t('인증 비밀번호 입력')"
                        :NonStatus="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="authSecret">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('인증 비밀번호 입력')" v-model="newChannel.channelOption.setting.server.authSecret" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
            </div>

            <div class="row form-group">
                <div class="col-8">
                    <label> {{ $t("요청 파라미터") }} </label>
                    <valid-input :vid="'요청 파라미터'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.reqParams"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_API_RULE" :placeholder="$t('요청 파라미터 입력')"
                        :NonStatus="true">
                    </valid-input>

                    <!-- <input type="text" min="1" class="form-control" :placeholder="$t('요청 파라미터 입력')" v-model="newChannel.channelOption.setting.server.reqParams" /> -->
                </div>
                <div class="col-4">
                    <label>{{ $t("데이터 유효기간") }}</label>
                    <select class="form-control" v-model="newChannel.channelOption.setting.server.validDataTime">
                        <option value="9999999">None</option>
                        <option value="60">1 min</option>
                        <option value="180">3 min</option>
                        <option value="300">5 min</option>
                        <option value="600">10 min</option>
                        <option value="1800">30 min</option>
                        <option value="3600">1 hour</option>
                        <option value="7200">2 hour</option>
                        <option value="10800">3 hour</option>
                    </select>
                </div>
            </div>
        </template>

        <!-- Aquaman & KieMecs Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && (newChannel.channelType === 'Aquaman' || newChannel.channelType === 'KieMecs')
                : newChannel.channelType === 'Aquaman' || newChannel.channelType === 'KieMecs'
        ">
            <div class="row form-group">
                <div class="col-3">
                    <label> {{ $t("프로토콜") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.server.protocol">
                        <option value="http">http</option>
                        <option value="https">https</option>
                    </select>
                </div>
                <div class="col-6">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.hostName"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="HostName">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('호스트명 입력')" v-model="newChannel.channelOption.setting.server.hostName" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호 입력')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
            </div>

            <div class="row form-group">
                <div class="col-9">
                    <label> {{ $t("인증 경로") }} </label>
                    <valid-input :vid="'인증 경로'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authUrl"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_ADDR_RULE" :placeholder="$t('인증을 위한 경로를 입력하세요.')"
                        :NonStatus="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="인증 URL">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('인증을 위한 경로를 입력하세요.')" v-model="newChannel.channelOption.setting.server.authUrl" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("인증 타입") }} </label>
                    <select name="" class="form-control" v-model="newChannel.channelOption.setting.server.authType">
                        <option value="Custom">Custom</option>
                    </select>
                </div>
            </div>

            <div class="row form-group">
                <div class="col-6">
                    <label> {{ $t("인증 아이디") }} </label>
                    <valid-input :vid="'인증 아이디'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authId"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_ID_RULE" :placeholder="$t('인증 아이디 입력')" :NonStatus="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="authId">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('인증 아이디 입력')" v-model="newChannel.channelOption.setting.server.authId" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-6">
                    <label> {{ $t("인증 비밀번호") }} </label>
                    <valid-input :vid="'인증 비밀번호'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authSecret"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_PASSWORD_RULE" :placeholder="$t('인증 비밀번호 입력')"
                        :NonStatus="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="authSecret">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('인증 비밀번호 입력')" v-model="newChannel.channelOption.setting.server.authSecret" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
            </div>

            <div class="row form-group">
                <div class="col-8">
                    <label> {{ $t("요청 파라미터") }} </label>
                    <valid-input :vid="'요청 파라미터'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.reqParams"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_API_RULE" :placeholder="$t('인증 파라미터 입력')"
                        :NonStatus="true">
                    </valid-input>
                    <!-- <input type="text" min="1" class="form-control" :placeholder="$t('요청 파라미터 입력')" v-model="newChannel.channelOption.setting.server.reqParams" /> -->
                </div>
                <div class="col-4">
                    <label>{{ $t("데이터 유효기간") }}</label>
                    <select class="form-control" v-model="newChannel.channelOption.setting.server.validDataTime">
                        <option value="9999999">없음</option>
                        <option value="60">1 min</option>
                        <option value="180">3 min</option>
                        <option value="300">5 min</option>
                        <option value="600">10 min</option>
                        <option value="1800">30 min</option>
                        <option value="3600">1 hour</option>
                        <option value="7200">2 hour</option>
                        <option value="10800">3 hour</option>
                    </select>
                </div>
            </div>
        </template>

        <!-- KieMecsMqtt Case -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'Mqtt'
                : newChannel.channelType === 'Mqtt'
        ">
            <div class="row form-group">
                <div class="col-5">
                    <label> {{ $t("호스트명") }} </label>
                    <valid-input :vid="'호스트명'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.host"
                        :rules="rules.COLLECT_DATA_MODBUS_HOST_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(5, 30)"
                        :placeholder="$t('IP주소나 호스트명을 입력하세요.')" :isMessage="true">
                    </valid-input>
                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Host">
            <input type="text" class="form-control" :class="classes" :placeholder="$t('IP주소나 호스트명을 입력하세요.')" v-model="newChannel.channelOption.setting.server.host" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-4">
                    <label> {{ $t("포트") }} </label>
                    <valid-input :vid="'포트'" :inputType="'number'"
                        :inputValue.sync="newChannel.channelOption.setting.server.port"
                        :rules="rules.COLLECT_DATA_MODBUS_PORT_RULE"
                        :errorMessage="validMessage.COMMON_NUMBER_LENGTH_VALID_MESSAGE(0, 65535)"
                        :placeholder="$t('포트번호를 입력하세요.')" :isMessage="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="Port">
            <input type="number" min="1" class="form-control" :class="classes" :placeholder="$t('포트번호를 입력하세요.')" v-model="newChannel.channelOption.setting.server.port" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-3">
                    <label> {{ $t("자동연결해제") }} </label>
                    <div class="checkbox checkbox-css">
                        <input type="checkbox" :id="'autoClose_' + newChannel.channelIdx"
                            v-model="newChannel.channelOption.setting.server.autoClose" />
                        <label :for="'autoClose_' + newChannel.channelIdx">
                            {{ $t("사용") }}
                        </label>
                    </div>
                </div>
            </div>
            <div class="row form-group">
                <div class="col-6">
                    <label> {{ $t("인증 아이디") }} </label>
                    <valid-input :vid="'인증 아이디'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authId"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_ID_RULE"
                        :errorMessage="validMessage.COMMON_NONKOREAN_VALID_MESSAGE(1, 50)"
                        :placeholder="$t('인증 아이디 입력')" :NonStatus="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="authId">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('인증 아이디 입력')" v-model="newChannel.channelOption.setting.server.authId" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
                <div class="col-6">
                    <label> {{ $t("인증 비밀번호") }} </label>
                    <valid-input :vid="'인증 비밀번호'" :inputType="'text'"
                        :inputValue.sync="newChannel.channelOption.setting.server.authSecret"
                        :rules="rules.COLLECT_DATA_KIEMECS_KL_AQUA_PASSWORD_RULE" :placeholder="$t('인증 비밀번호 입력')"
                        :NonStatus="true">
                    </valid-input>

                    <!-- <vvp rules="required" v-slot="{ errors, classes }" name="authSecret">
            <input type="text" min="1" class="form-control" :class="classes" :placeholder="$t('인증 비밀번호 입력')" v-model="newChannel.channelOption.setting.server.authSecret" />
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </vvp> -->
                </div>
            </div>
        </template>

        <!-- Virtual Case  -->
        <template v-if="
            mode === 'panel'
                ? activeTab === 1 && newChannel.channelType === 'Virtual'
                : newChannel.channelType === 'Virtual'
        ">
            <div class="row form-group" v-if="newChannel.channelType === 'Virtual'">
                <div class="col-lg-12">
                    <label for="">{{ $t("가상 채널에는 옵션이 없습니다.") }}</label>
                </div>
            </div>
        </template>

        <!-- 계측기 정보 추가 끝 -->
        <div v-if="activeTab === 1 || mode === 'popup'" slot="modal-footer" class="w-100 mt-3">
            <button class="btn btn-danger" v-if="isUpdate && (isEmpty(childrens) || childrens == 0)"
                @click="onDeleteChannel()">
                <i class="fa fa-times"></i> {{ $t("삭제") }}
            </button>
            <button class="btn btn-primary pull-right" @click="onValidate(v, onSaveChannel, newChannel)">
                <i class="fa fa-save"></i> {{ $t("저장") }}
            </button>
        </div>

        <!-- 계측기 정보 팝업 -->
        <FacilityInfoPopup ref="FacilityInfoPopup" :facilityIdx="getFacilityIdx" />
    </vvo>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import * as rules from "@src/consts/ruleConsts.js";
import * as validMessage from "@src/consts/validMessageConsts.js";
import * as popupMessages from "@src/consts/popupMessageConsts";
import * as XLSX from "xlsx";
import { mapState } from "vuex";
import "vue-good-table/dist/vue-good-table.css";
import { downloadExcel } from "@src/views/new/point-management/point-mgmt/panel/exportExcel.js";
import TableList from "@src/views/component/v2.1/ListDetailView/TableList.vue";
import { extractFilenameFromResponse } from "@src/utils/exportExcelTitle.js";
import FacilityInfoPopup from "./FacilityInfoPopup.vue";

export default {
    props: ["serverIdx", "channelIdx", "childrens", "save-done", "mode", "childData"],
    components: { TableList, FacilityInfoPopup },
    data() {
        return {
            // classes:"", // 오류..
            newChannel: {
                serverIdx: this.serverIdx,
                channelType: "ModbusTcp",
                enable: false,
                channelOption: this.makeChannelOpt("ModbusTcp"),
            },
            rules,
            validMessage,
            popupMessages,
            excelUploadErrorList: [],

            fileName: "",
            headerMappings: [],

            // 서브 모듈 업로드 엑셀파일
            pointUploadXlsx: null,

            /* 탭 상태 */
            activeTab: 0,

            /* 탭 정의 */
            tabs: [
                {
                    label: "하위 계측기 목록",
                    icon: "fa fa-list",
                },
                {
                    label: "통신채널 정보",
                    icon: "fa fa-info-circle",
                },
                {
                    label: "부하 목록 관리",
                    icon: "fa fa-upload",
                },
            ],

            /* 서브 탭 정의 */
            subTabs: [
                {
                    label: "계측기 설정정보",
                    icon: "fa fa-history",
                },
                {
                    label: "계측기 관리정보",
                    icon: "fa fa-cogs",
                },
            ],

            /* 서브 탭 상태 */
            activeSubTab: 0,

            showDescList: ["Code", "Zone"],
            activeDropdownIndex: null,

            // 계측기 관리정보 관련 데이터
            selectedGroupField: null,

            // 선택된 계측기
            selectedFacilityRow: null,

            // 필수 필드
            essentialField: ["parentFacilityName", "subFacilityName", "feeder", "propName"],

            // 계측기 관리정보 관련 데이터
            facilityOpInfoList: [],
        };
    },
    computed: {
        ...mapState({
            commonCodes: (state) => state.commonCodes,
            zoneInfo: (state) => state.zoneInfo,
        }),
        channelTransCodes() {
            return [
                {
                    field: "isUse",
                    codes: [
                        { value: true, text: "사용" },
                        { value: false, text: "미사용" },
                    ],
                },
            ];
        },
        convertHeaderOptions() {
            // 스토어에서 가져온 옵션들
            const storeOptions =
                this.commonCodes?.headerMapping.map((v) => ({ value: v.value, text: v.text, Desc: v.options.Desc })) ||
                [];

            // 기본 옵션과 스토어 옵션을 합침
            return [{ value: null, text: "선택" }, ...storeOptions];
        },
        isUpdate() {
            return !this.isEmpty(this.channelIdx);
        },
        equipmentColumns() {
            return [
                {
                    label: "계측기명",
                    field: "facilityName",
                    sortable: true,
                    width: "200px",
                },
                {
                    label: "사용여부",
                    field: "isUse",
                    sortable: true,
                    width: "100px",
                },
                {
                    label: "장치 고유번호",
                    field: "deviceId",
                    sortable: true,
                    width: "120px",
                },
                {
                    label: "활용 목적",
                    field: "purpsUseCode",
                    sortable: true,
                    width: "120px",
                },
                {
                    label: "계측기 유형",
                    field: "meterTypeCode",
                    sortable: true,
                    width: "120px",
                },
                {
                    label: "적용구역",
                    field: "applyZoneIdx",
                    sortable: true,
                    width: "120px",
                },
                {
                    label: "설치일자",
                    field: "instDate",
                    sortable: true,
                    width: "120px",
                },
            ];
        },
        equipmentRows() {
            if (!this.childData || !this.childData.children) {
                return [];
            }

            return this.childData.children.map((child) => {
                const data = child.data || {};
                return {
                    facilityIdx: data.facilityIdx || null,
                    facilityName: data.facilityName || "-",
                    isUse: data.isUse || false,
                    deviceId: data.deviceId || "-",
                    purpsUseCode: this.findPurpsUseCodeName(data.purpsUseCode) || "-",
                    meterTypeCode: this.findMeterTypeCodeName(data.meterTypeCode) || "-",
                    applyZoneIdx: this.findZoneInfoName(data.applyZoneIdx) || "-",
                    instDate: data.instDate || null,
                };
            });
        },

        // 계측기 관리정보 컬럼 정의
        facilityManagementColumns() {
            return [
                {
                    label: "계측기명",
                    field: "facilityName",
                    sortable: true,
                },

                {
                    label: "제조사",
                    field: "manufacturer",
                    sortable: true,
                },
                {
                    label: "모델명",
                    field: "modelNumber",
                    sortable: true,
                },
                {
                    label: "시리얼 넘버",
                    field: "serialNumber",
                    sortable: true,
                },
                {
                    label: "설치장소",
                    field: "location",
                    sortable: true,
                },
                {
                    label: "정격용량",
                    field: "ratedCapacity",
                    sortable: true,
                },
                {
                    label: "정격 COP",
                    field: "ratedCOP",
                    sortable: true,
                },
                {
                    label: "수정일시",
                    field: "updDt",
                    sortable: true,
                },
                {
                    label: "설명",
                    field: "description",
                    sortable: true,
                },
            ];
        },

        facilityManagementRows() {
            if (!this.facilityOpInfoList || this.facilityOpInfoList.length === 0) {
                return [];
            }

            return this.facilityOpInfoList.map((item) => ({
                facilityName: item.facilityName || "-",
                manufacturer: item.opInfo?.manufacturer || "-",
                modelNumber: item.opInfo?.modelNumber || "-",
                serialNumber: item.opInfo?.serialNumber || "-",
                location: item.opInfo?.location || "-",
                ratedCapacity: item.opInfo?.ratedCapacity
                    ? `${item.opInfo?.ratedCapacity} ${item.opInfo?.capacityUnit || ""}`
                    : "-",
                ratedCOP: item.opInfo?.ratedCOP || "-",
                updDt: this.formatRegDt(item.opInfo?.updDt) || "-",
                description: this.codeChangelengthText(item.opInfo?.description, 7) || "-",
                facilityIdx: item.facilityIdx || "-",
            }));
        },

        filteredFacilityManagementRows() {
            if (!this.selectedGroupField) {
                return this.facilityManagementRows;
            }

            return this.facilityManagementRows.filter((row) => {
                return row[this.selectedGroupField] && row[this.selectedGroupField] !== "-";
            });
        },

        facilityManagementTransCodes() {
            return [];
        },
        getFacilityIdx() {
            return this.selectedFacilityRow?.facilityIdx;
        },
    },
    watch: {
        channelIdx: function () {
            this.initSetting();
        },
        activeSubTab: function (newVal) {
            if (newVal === 1) {
                this.searchFacilityOpInfo();
            }
        },
    },
    mounted() {
        this.initSetting();
    },
    methods: {
        searchFacilityOpInfo() {
            backEndApi.facility
                .searchFacilityOpInfo({ channelIdx: this.childData.data.channelIdx })
                .then(({ data }) => {
                    this.facilityOpInfoList = data;
                });
        },
        // regDt를 연월일까지만 표시
        formatRegDt(val) {
            if (global.xe.isEmpty(val)) return "-";

            // 문자열인 경우 Date 객체로 변환
            if (typeof val === "string") {
                const date = new Date(val);
                if (isNaN(date.getTime())) return val; // 유효하지 않은 날짜면 원본 반환
                return date.toISOString().split("T")[0]; // yyyy-MM-dd 형식으로 반환
            }

            // 이미 Date 객체인 경우
            return val.format("yyyy-MM-dd");
        },
        // 텍스트가 너무 길경우 자르고 ... 처리
        codeChangelengthText(val, len = 50, defaultStr = "") {
            if (global.xe.isEmpty(val)) return defaultStr;
            if (val.length > len) return val.substring(0, len) + "...";
            return val;
        },
        findPurpsUseCodeName(value) {
            return this.commonCodes.purpsUseCode.find((v) => v.value === value)?.text;
        },
        findZoneInfoName(value) {
            return this.zoneInfo.find((v) => v.value === value)?.text;
        },
        findMeterTypeCodeName(value) {
            return this.commonCodes.meterTypeCode.find((v) => v.value === value)?.text;
        },
        findClbrPeriodCodeName(value) {
            return this.commonCodes.clbrPeriodCode.find((v) => v.value === value)?.text;
        },
        showSubDesc(value) {
            return this.showDescList.includes(this.convertHeaderOptions.find((v) => v.value === value)?.Desc);
        },
        getSubDescList(value, desc) {
            if (desc == "Code") {
                return this.commonCodes[value];
            } else if (desc == "Zone") {
                return this.zoneInfo;
            }
            return [];
        },
        getSubDescListItems(convertHeader) {
            const option = this.convertHeaderOptions.find((v) => v.value === convertHeader);
            if (!option || !option.Desc) return [];

            const desc = option.Desc;
            const list = this.getSubDescList(convertHeader, desc);

            if (Array.isArray(list)) {
                return list;
            } else if (list && list.options) {
                return list.options;
            }
            return [];
        },
        showDropdown(index) {
            this.activeDropdownIndex = index;
        },
        hideDropdown() {
            this.activeDropdownIndex = null;
        },
        initExcelData() {
            this.headerMappings = [];
            this.pointUploadXlsx = null;
            this.excelUploadErrorList = [];

            this.fileName = "";
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        findHeaderTextFromCommonCodes(value) {
            const header = this.commonCodes.headerMapping.find((header) => header.text === value);
            return header ? header.value : null;
        },
        mappingDefaultHeader() {
            // fieldMapping에 있는 항목 처리
            if (this.newChannel.fieldMapping) {
                Object.entries(this.newChannel.fieldMapping).forEach(([key, value]) => {
                    const header = this.headerMappings.find((header) => header.excelHeader === key);
                    if (header) {
                        // 1순위로는 기존에 존재하는 매핑정보를 사용한다.
                        header.convertHeader = value;
                    }
                });
            } else if (!this.newChannel.fieldMapping) {
                // 2순위로 공통 코드에서 찾는다. 3순위로는 데이터 보관으로 설정
                this.headerMappings.forEach((header) => {
                    header.convertHeader = this.findHeaderTextFromCommonCodes(header.excelHeader) || "archive";
                });
            }

            // extraFields에 있는 항목 처리
            if (Array.isArray(this.newChannel.extraFields)) {
                this.newChannel.extraFields.forEach((extraField) => {
                    const header = this.headerMappings.find((header) => header.excelHeader === extraField);
                    if (header) {
                        header.convertHeader = "archive";
                    }
                });
            }
        },
        selectFile() {
            this.$refs.fileInput.click();
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                // 새로운 파일이 업로드되면 오류 리스트 초기화
                this.initExcelData();
                this.fileName = file.name;
                this.pointUploadXlsx = file;

                const reader = new FileReader();
                reader.onload = (e) => {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    // Set 객체를 사용해서 모든 탭에서 유니크한 헤더들을 수집
                    const uniqueHeaders = new Set();

                    // 모든 시트(탭)를 순회하며 헤더 추출
                    workbook.SheetNames.forEach((sheetName) => {
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                        if (jsonData.length > 0 && Array.isArray(jsonData[0])) {
                            // 각 시트의 첫 번째 행(헤더)을 Set에 추가
                            jsonData[0].forEach((header) => {
                                if (header && header.toString().trim() !== "") {
                                    uniqueHeaders.add(header.toString().trim());
                                }
                            });
                        }
                    });

                    // Set을 배열로 변환하여 headerMappings 생성
                    if (uniqueHeaders.size > 0) {
                        this.headerMappings = Array.from(uniqueHeaders).map((header) => ({
                            excelHeader: header,
                            convertHeader: null,
                        }));
                        this.mappingDefaultHeader();
                    }
                };

                reader.readAsArrayBuffer(file);
            }
        },
        /* 탭 수동 클릭 */
        handleTabClick(idx) {
            if (this.activeTab === idx) return;
            this.activeTab = idx;
        },

        /* 서브 탭 클릭 */
        handleSubTabClick(idx) {
            if (this.activeSubTab === idx) return;
            this.activeSubTab = idx;
        },

        /* 서브 탭 select 변경 */
        handleSubTabChange() {
            // select 박스 값이 변경되면 자동으로 activeSubTab이 업데이트됨
            // 추가 로직이 필요한 경우 여기에 작성
        },

        // 파일 업로드 트리거
        triggerFileUpload() {
            this.$refs.fileInput.click();
        },

        // 파일 선택 시 처리
        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.uploadFacilityOpInfo(file);
                // 파일 입력 초기화
                this.$refs.fileInput.value = "";
            }
        },

        // 파일 업로드 처리
        uploadFacilityOpInfo(file) {
            if (!file) {
                this.alertNoti("파일을 선택해주세요.");
                return;
            }

            // 파일 형식 검증
            const allowedTypes = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
                "application/vnd.ms-excel", // .xls
            ];

            if (!allowedTypes.includes(file.type)) {
                this.alertNoti("엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.");
                return;
            }

            // 업로드 진행
            this.alertQuestion("파일을 업로드하시겠습니까?").then((result) => {
                if (!result.value) return;
                this.executeFileUpload(file);
            });
        },

        // 파일 다운로드 처리
        downloadFacilityOpInfo() {
            this.alertQuestion("계측기 관리정보를 다운로드하시겠습니까?").then((result) => {
                if (!result.value) return;

                this.executeFileDownload(this.childData.data.channelIdx);
            });
        },

        // 계측기 데이터 새로고침
        refreshFacilityData() {
            // 부모 컴포넌트에 데이터 새로고침 요청
            this.$emit("refresh-data");
        },

        // 실제 파일 업로드 실행
        async executeFileUpload(file) {
            try {
                const { data } = await backEndApi.facility.uploadFacilityOpInfo(file);

                this.alertNoti(data.message);
                // 업로드 성공 후 데이터 새로고침
                this.refreshFacilityData();
            } catch (error) {
                console.error("Upload error:", error);
                this.alertNoti("파일 업로드 중 오류가 발생했습니다.");
                throw error;
            }
        },

        // 실제 파일 다운로드 실행
        async executeFileDownload(channelIdx) {
            try {
                const response = await backEndApi.facility.downloadFacilityOpInfo({ channelIdx: channelIdx });

                if (response.status === 200 && response.data) {
                    let fileName = `계측기 관리정보.xlsx`;

                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;

                    fileName = extractFilenameFromResponse(response, fileName);
                    link.download = fileName;

                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.alertNoti("계측기 관리정보 다운로드가 시작되었습니다.");
                } else {
                    throw new Error(response.data.message);
                }
            } catch (e) {
                this.alertWarning(e.message);
                console.error(e);
                throw e;
            }
        },

        handleRowClick(row) {
            // facilityOpInfoList에서 해당 facilityIdx를 가진 원본 데이터를 찾아서 설정
            const originalData = this.facilityOpInfoList.find((item) => item.facilityIdx === row.facilityIdx);
            this.selectedFacilityRow = originalData || row;
            this.openFacilityInfoPopup();
        },
        openFacilityInfoPopup() {
            if (this.$refs.FacilityInfoPopup && this.selectedFacilityRow) {
                this.$refs.FacilityInfoPopup.show();
            }
        },

        /* 현재 활성 탭에 따른 데이터 반환 */
        getCurrentRows() {
            return this.activeSubTab === 0 ? this.equipmentRows : this.facilityManagementRows;
        },

        /* 엑셀 다운로드 버튼 클릭 핸들러 */
        handleExcelDownload() {
            if (this.activeSubTab === 0) {
                this.exportEquipmentToExcel();
            } else if (this.activeSubTab === 1) {
                this.exportFacilityManagementToExcel();
            }
        },

        /* 파일 업로드 */
        async uploadFile() {
            try {
                if (!this.pointUploadXlsx) {
                    this.alertNoti("업로드할 파일을 먼저 선택해주세요.");
                    return;
                }
                if (this.essentialFieldCheck()) {
                    await this.executeExcelUpload();
                    return;
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        essentialFieldCheck() {
            let isValid = true;
            this.essentialField.forEach((field) => {
                const found = this.headerMappings.find((mapping) => mapping.convertHeader === field);
                if (!found) {
                    this.alertWarning(`필수 필드(${this.convertHeaderMappingToText(field)})가 선택되지 않았습니다.`);
                    isValid = false;
                }
            });
            return isValid;
        },
        convertHeaderMappingToText(convertHeader) {
            return this.convertHeaderOptions.find((v) => v.value === convertHeader)?.text;
        },
        /* 부하목록 다운로드 */
        async downloadLoadList() {
            await this.downloadPointInfoExcel();
        },

        /* 맵핑정보 저장 */
        async saveMappingInfo() {
            if (!this.headerMappings || this.headerMappings.length === 0) {
                this.alertNoti("저장할 맵핑 정보가 없습니다.");
                return;
            }
            this.updateChannel();
        },

        async downloadPointInfoExcel() {
            try {
                const response = await backEndApi.facility.downloadPointInfoByChannel(this.serverIdx, this.channelIdx);

                if (response.status === 200 && response.data) {
                    let fileName = `부하목록.xlsx`;

                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;

                    fileName = extractFilenameFromResponse(response, fileName);
                    link.download = fileName;

                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.alertNoti("부하목록 다운로드가 시작되었습니다.");
                } else {
                    throw new Error(response.data);
                }
            } catch (e) {
                this.alertWarning(e.message);
                console.error(e);
                throw e;
            }
        },

        // 엑셀 업로드 실행
        async executeExcelUpload() {
            // 매핑 정보 업데이트
            let mappingInfo = this.updateChannelMapping();

            try {
                const result = await backEndApi.facility.uploadPointInfoByChannel(
                    this.serverIdx,
                    this.channelIdx,
                    this.pointUploadXlsx,
                    mappingInfo.fieldMapping,
                    mappingInfo.extraFields
                );

                if (result.status !== 200) {
                    if (result.data.detail?.errors?.length > 0) {
                        this.excelUploadErrorList = result.data.detail.errors;
                    }
                    throw new Error(result.data.message);
                } else if (result.status === 200) {
                    this.alertNoti(result.data.message);
                    this.$emit("save-done");
                }
                return;
            } catch (e) {
                this.alertWarning(e.message);
                console.error(e);
                throw e;
            }
        },

        initSetting() {
            // this.newChannel = {
            //   serverIdx: this.serverIdx,
            //   channelType: "ModbusTcp",
            //   enable: false,
            //   channelOption: this.makeChannelOpt("ModbusTcp"),
            //   meterTypeCode : "",
            //   clbrPeriodCode : ""
            // };

            if (this.isUpdate) {
                backEndApi.channel.getChannel(this.channelIdx).then(({ data }) => {
                    if (this.$err(data)) return;

                    data.channelOption = this.merge(this.makeChannelOpt(data.channelType), data.channelOption);

                    this.newChannel = data;
                });
            }

            // 계측기관리 검교정 관련 selectbox
            // this.getSmallCodeByChannel();
        },
        selectChannelType() {
            this.newChannel.channelOption = this.makeChannelOpt(this.newChannel.channelType);
        },
        onSaveChannel() {
            this.isUpdate ? this.updateChannel() : this.insertChannel();
        },
        insertChannel() {
            this.alertQuestion(popupMessages.COMMON_SAVE_CONFIRM_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.channel.insertChannel(this.newChannel).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);
                    this.$emit("save-done");
                });
            });
        },
        updateChannel() {
            // 매핑 정보 업데이트
            this.updateChannelMapping();

            this.alertQuestion(popupMessages.COMMON_UPDATE_CONFIRM_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.channel.updateChannel(this.newChannel).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.COMMON_UPDATE_POPUP_MESSAGE);
                    this.$emit("save-done");
                });
            });
        },
        updateChannelMapping() {
            if (!this.headerMappings || this.headerMappings.length === 0) return;

            let notSaveList = this.headerMappings.filter((header) => header.convertHeader != "archive");
            let saveList = this.headerMappings.filter((header) => header.convertHeader == "archive");

            const fieldMapping = {};
            notSaveList.forEach((item) => {
                fieldMapping[item.excelHeader] = item.convertHeader;
            });

            return {
                fieldMapping: fieldMapping,
                extraFields: saveList.map((header) => header.excelHeader),
            };
        },

        onDeleteChannel() {
            // TODO: 하위 정보가 있으면 삭제할 수 없습니다.

            this.alertConfirmWarning(popupMessages.COMMON_DELETE_CONFIRM_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                backEndApi.channel.deleteChannel(this.newChannel.channelIdx).then(({ data }) => {
                    if (this.$err(data)) return;

                    this.alertNoti(popupMessages.COMMON_DELETE_POPUP_MESSAGE);
                    this.$emit("save-done");
                });
            });
        },

        makeChannelOpt(channelType) {
            let channelOption = {
                scan: 60000,
                timeout: 3000,
                reporting: "WebAPI",
                isVirtualMode: false,
            };

            // ADD_DRIVER
            if (channelType === "ModbusRtu" || channelType === "RS485") {
                // "setting":{"comPort":{"dev":"COM1", "baudRate":9600,"parity":"none","dataBits":8,"stopBits":1},"writing":{"retry":5,"afterFailed":500}}} -->
                return Object.assign(channelOption, {
                    setting: {
                        comPort: {
                            dev: "",
                            baudRate: 9600,
                            parity: "none",
                            dataBits: 8,
                            stopBits: 1,
                            autoClose: true,
                        },
                    },
                });
            } else if (channelType === "ModbusTcp") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            host: "",
                            port: "502",
                            autoClose: true,
                        },
                    },
                });
            } else if (channelType === "SocketTcp") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            host: "",
                            port: "3000",
                            autoClose: true,
                            serial: "",
                        },
                    },
                });
            } else if (channelType === "SocketUdp") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            host: "",
                            port: "10262",
                            autoClose: true,
                        },
                    },
                });
            } else if (channelType === "BacnetTcp") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            host: "",
                            port: "47808",
                            autoClose: true,
                        },
                    },
                });
            } else if (channelType === "KevinLab") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            protocol: "http",
                            hostName: "www.lbems.com",
                            port: "80",
                            authUrl: "/kbet/login.php",
                            authType: "JWT",
                            encryptMethod: "aes-256-cbc",
                            encodingMethod: "base64",
                            encryptKey: "",
                            authId: "kbetadmin",
                            authSecret: "",
                            validDataTime: "9999999",
                            reqParams: "",
                        },
                    },
                });
            } else if (channelType === "IBeems") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            protocol: "http",
                            hostName: "121.135.187.92",
                            port: "28082",
                            serviceToken: "",
                            validDataTime: "9999999",
                            reqParams: "",
                        },
                    },
                });
            } else if (channelType === "Aquaman") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            protocol: "https",
                            hostName: "ftlab-vm-api.com",
                            port: "443",
                            authUrl: "/api/rsv2/customers/login",
                            authType: "Custom",
                            authId: "",
                            authSecret: "",
                            validDataTime: "9999999",
                            reqParams: "",
                        },
                    },
                });
            } else if (channelType === "KieMecs") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            protocol: "https",
                            hostName: "openapi.thekie.com",
                            port: "443",
                            authUrl: "/v1/auth",
                            authType: "Custom",
                            authId: "",
                            authSecret: "",
                            validDataTime: "9999999",
                            reqParams: "",
                        },
                    },
                });
            } else if (channelType === "Mqtt") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {
                            host: "mqtt.mecs.thekie.com",
                            port: "9073",
                            autoClose: true,
                        },
                    },
                });
            } else if (channelType === "Virtual") {
                return Object.assign(channelOption, {
                    setting: {
                        server: {},
                    },
                });
            }

            return channelOption;
        },
        // getSmallCodeByChannel(){
        //   backEndApi.publicCode.getSmallCodeList("meterTypeCode").then(({ data }) => {
        //     if(!data.errno){
        //       this.meterTypeCodeList = data;
        //     }
        //   });

        //   backEndApi.publicCode.getSmallCodeList("clbrPeriodCode").then(({ data }) => {
        //     if(!data.errno){
        //       this.clbrPeriodCodeList = data;
        //     }
        //   });
        // }
        formatDate(dateString) {
            if (!dateString) return "-";

            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return "-";

                return date.toLocaleDateString("ko-KR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                });
            } catch (error) {
                return "-";
            }
        },

        // 엑셀 다운로드 메서드
        exportEquipmentToExcel() {
            if (!this.equipmentRows || this.equipmentRows.length === 0) {
                this.alertNoti("다운로드할 데이터가 없습니다.");
                return;
            }

            // equipmentColumns를 참조하여 헤더 매핑 생성
            const headerMap = {};
            this.equipmentColumns.forEach((column) => {
                headerMap[column.field] = column.label;
            });

            // 파일명 생성 (현재 날짜 포함)
            const currentDate = new Date().toISOString().slice(0, 10);
            const fileName = `하위계측기목록_${this.newChannel.channelName || "통신채널"}_${currentDate}`;

            // exportExcel 함수 호출
            downloadExcel(this.equipmentRows, fileName, headerMap);

            this.alertNoti("하위 계측기 목록 엑셀 다운로드가 시작되었습니다.");
        },

        // 계측기 관리정보 엑셀 다운로드 메서드
        exportFacilityManagementToExcel() {
            if (!this.facilityManagementRows || this.facilityManagementRows.length === 0) {
                this.alertNoti("다운로드할 데이터가 없습니다.");
                return;
            }

            // facilityManagementColumns를 참조하여 헤더 매핑 생성
            const headerMap = {};
            this.facilityManagementColumns.forEach((column) => {
                headerMap[column.field] = column.label;
            });

            // 파일명 생성 (현재 날짜 포함)
            const currentDate = new Date().toISOString().slice(0, 10);
            const fileName = `계측기관리정보_${this.newChannel.channelName || "통신채널"}_${currentDate}`;

            // exportExcel 함수 호출
            downloadExcel(this.facilityManagementRows, fileName, headerMap);

            this.alertNoti("계측기 관리정보 엑셀 다운로드가 시작되었습니다.");
        },
    },
};
</script>

<style scoped>
.channel-popup-panel-mode {
    width: 80%;
    height: 45vh;
    padding: 15px;
    border-radius: 10px;
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 8px;
}

.mapping-ellipsis {
    font-size: 14px;
    color: #6c757d;
    cursor: pointer;
    margin-right: 0.2rem;
    padding: 0.3rem;
}

.mapping-ellipsis:hover {
    color: #002060;
    background-color: #f1f3f4;
    border-radius: 50%;
    padding: 0.3rem;
    display: flex;
    justify-content: center;
    align-items: center;
}

.mapping-dropdown-container {
    position: relative;
    display: inline-block;
}

.mapping-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 150px;
    max-height: 300px;
    overflow-y: auto;
    padding: 0.5rem 0;
    z-index: 99999;
}

.dropdown-item {
    padding: 0.5rem 1rem;
    font-size: 0.7rem;
    color: #495057;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #f8f9fa;
}

.dropdown-item:last-child {
    border-bottom: none;
}

.detail-title {
    padding: 0 !important;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 1rem;
    font-weight: 600;
}

.title {
    font-size: 14px;
    font-weight: 600;
    margin-left: 10px;
    margin-right: 10px;
}

.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0px 10px 0px 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title {
    background-color: #ff6d00;
    color: #fff;
}

.badge.id {
    background-color: #546e7a;
    color: #fff;
}

.badge.option {
    background-color: #cfd8dc;
    color: #000;
}

/* tabs */
.custom-tabs {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
    background: #fff;
}

.custom-tab {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 500;
    background: transparent;
    color: #6c757d;
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 120px;
    justify-content: center;
}

.custom-tab:hover {
    color: #495057;
    background: #f8f9fa;
}

.custom-tab.active {
    color: #002060;
    font-weight: 600;
    border-bottom-color: #002060;
}

/* tab container */
.tab-container {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    border-radius: 8px;
}

.tab-content-container {
    flex: 1 1 auto;
    overflow: auto;
    overflow-x: hidden;
    background: #fff;
    border-radius: 0 0 8px 8px;
    position: relative;
    padding: 1rem;
    border-top: 1px solid #f1f3f4;
}

.load-management-content {
    padding: 0;
}

.section-title {
    font-size: 13px;
    font-weight: 600;
    margin: 0;
    color: #495057;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: 0.5rem;
    padding: 0.5rem 0;
}

.upload-section {
    margin-bottom: 1.5rem;
    padding: 0;
}

.upload-header {
    padding: 0.5rem 0;
    margin-bottom: 0.5rem;
}

.upload-area {
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 14px;
    transition: all 0.3s ease;
}

.upload-area:hover {
    border-color: #bdbdbd;
    background: #f1f3f4;
}

.file-input-group {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.file-display {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e9ecef;
}

.file-icon {
    font-size: 1.5rem;
    color: #28a745;
}

.file-name {
    font-size: 0.9rem;
    color: #495057;
    font-weight: 500;
}

.button-group {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.mapping-section {
    margin-bottom: 1.5rem;
    padding: 0;
}

.mapping-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    margin-bottom: 0.5rem;
}

.mapping-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.mapping-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 0.5rem;
}

.mapping-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    transition: all 0.3s ease;
}

.mapping-item:hover {
    border-color: #002060;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mapping-source,
.mapping-target {
    flex: 1;
    display: flex;
    gap: 0.2rem;
    width: 100%;
    justify-content: space-between;
    align-items: center;
}

.mapping-value {
    font-size: 12px;
    font-weight: 700;
    color: #495057;
    padding: 0.3rem;
    border-radius: 4px;
    line-height: 1.3;
    text-align: left;
    max-width: 15em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mapping-select {
    border: 1px solid #e9ecef;
    border-radius: 4px;
    height: 30px;
    padding: 0.3rem;
    background: #f8f9fa;
    font-size: 0.8rem;
    font-weight: 500;
    color: #495057;
    line-height: 1.3;
    appearance: auto;
    -webkit-appearance: auto;
    -moz-appearance: auto;
}

/* 오류 섹션 */
.error-section {
    margin-bottom: 1.5rem;
    padding: 0;
}

.error-header {
    margin-bottom: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e9ecef;
}

.error-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.error-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 8px;
    border-left: 4px solid #e53e3e;
}

.error-row {
    flex-shrink: 0;
}

.error-badge {
    background: #e53e3e;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
}

.error-message {
    flex: 1;
    font-size: 0.9rem;
    color: #c53030;
    line-height: 1.4;
}

.equipment-list-content {
    padding: 0;
    width: 100%;
}

.equipment-container {
    background: #ffffff;
    border-radius: 8px;
    padding: 0;
    min-height: 200px;
}

.total-count {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    background: #f8f9fa;
    padding: 4px 12px;
    border-radius: 16px;
}

.table-container {
    background: #ffffff;
    border-radius: 6px;
    overflow: hidden;
}

.text-center {
    text-align: center !important;
}

.facility-name {
    font-weight: 600;
    color: #495057;
}

.status-badge .status-active {
    background: #d4edda;
    color: #155724;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.status-badge .status-inactive {
    background: #f8d7da;
    color: #721c24;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.date-format {
    color: #6c757d;
    font-size: 12px;
}

.no-equipment {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
}

.text-left {
    text-align: left !important;
}

.text-right {
    text-align: right !important;
}

/* 서브 탭 버튼 스타일 */
.sub-tab-button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0.5rem 0;
}

.sub-tab-buttons {
    display: flex;
    gap: 0.5rem;
}

.sub-tab-button {
    padding: 0.3125rem 0.625rem;
    border: 1px solid #e9ecef;
    background: transparent;
    color: #6c757d;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.75rem;
    line-height: 1.4;
    font-weight: 600;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.3rem;
}

.sub-tab-button:focus {
    outline: none;
    border: none;
}

.sub-tab-button:hover {
    background: #f8f9fa;
    /* border-color: #002060; */
    color: #002060;
}

.sub-tab-button.active {
    background: #002060;
    border-color: #002060;
    color: white;
}

/* 엑셀 다운로드 버튼 스타일 */
.excel-download-btn {
    margin-left: 0.5rem;
}

/* 서브 탭 select 스타일 */
.controls-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 15px 0 0 0;
    padding: 15px;
    background: #f2f3f4;
    border-radius: 8px 8px 0 0;
    border: 1px solid #e9ecef;
}

.facility-mode-controls {
    margin-left: 8px;
}

.tab-label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    margin: 0;
    white-space: nowrap;
}

.tab-select {
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    background: #ffffff;
    color: #495057;
    border: 1px solid #ced4da;
    border-radius: 6px;
    cursor: pointer;
    min-width: 160px;
    transition: all 0.2s ease;
    margin-left: 8px;
}

.tab-select:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.tab-select:hover {
    border-color: #adb5bd;
}

/* 엑셀 섹션 */
.excel-section {
    display: flex;
    gap: 8px;
}

.excel-btn {
    padding: 8px 16px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    border: none;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #ffffff;
    min-width: 100px;
}

.excel-btn:hover {
    transform: translateY(-1px);
}

.excel-btn.upload {
    background: #28a745;
}

.excel-btn.upload:hover {
    background: #218838;
}

.excel-btn.download {
    background: #007bff;
}

.excel-btn.download:hover {
    background: #0056b3;
}

.mapping-item.filled {
    border: 1px solid #42a5f5;
    background: #f8faff;
    box-shadow: 0 2px 6px rgba(13, 110, 253, 0.15);
}

.mapping-item.filled:hover {
    border-color: #0b5ed7;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
}
</style>
